import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, useEffect, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router";
import "./index.css";
import { ApiError, refresh, session } from "./lib/api";
import { Ads, AdEdit, AdReportPage } from "./pages/Ads";
import Cohorts from "./pages/Cohorts";
import Coop from "./pages/Coop";
import EventEdit from "./pages/EventEdit";
import Events from "./pages/Events";
import Inbox from "./pages/Inbox";
import Login from "./pages/Login";
import { MemberDetail, Members } from "./pages/Members";
import More from "./pages/More";
import { Categories, PlaceEdit, Places } from "./pages/Places";
import System from "./pages/System";
import { ErrorBoundary, initSentry } from "./lib/sentry";
import { Shell } from "./ui/Shell";

initSentry();

const qc = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: (n, e) => !(e instanceof ApiError && e.status < 500) && n < 2 } },
});

/** Resume the session from the httpOnly refresh cookie; otherwise send to sign-in. */
function RequireAdmin({ children }: { children: ReactNode }) {
  const [state, setState] = useState<"checking" | "in" | "out">(session.signedIn ? "in" : "checking");
  useEffect(() => {
    session.onSignedOut(() => setState("out"));
    if (state === "checking") void refresh().then((ok) => setState(ok ? "in" : "out"));
  }, [state]);
  if (state === "checking") return <div className="grid min-h-dvh place-items-center text-muted">Loading…</div>;
  if (state === "out") return <Navigate to="/login" replace />;
  return children;
}

const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  {
    element: <RequireAdmin><Shell /></RequireAdmin>,
    children: [
      { path: "/", element: <Inbox /> },
      { path: "/events", element: <Events /> },
      { path: "/events/:id", element: <EventEdit /> },
      { path: "/members", element: <Members /> },
      { path: "/members/:id", element: <MemberDetail /> },
      { path: "/ads", element: <Ads /> },
      { path: "/ads/:id", element: <AdEdit /> },
      { path: "/ads/:id/report", element: <AdReportPage /> },
      { path: "/places", element: <Places /> },
      { path: "/places/:id", element: <PlaceEdit /> },
      { path: "/categories", element: <Categories /> },
      { path: "/cohorts", element: <Cohorts /> },
      { path: "/coop", element: <Coop /> },
      { path: "/system", element: <System /> },
      { path: "/more", element: <More /> },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary
      fallback={
        <div className="grid min-h-dvh place-items-center p-6 text-center">
          <div>
            <h1 className="text-2xl font-bold">Something went wrong</h1>
            <p className="mt-2 text-muted">We've been notified. Reload the page to keep going.</p>
          </div>
        </div>
      }
    >
      <QueryClientProvider client={qc}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </ErrorBoundary>
  </StrictMode>,
);
